/* eslint-disable @typescript-eslint/no-var-requires */
const { AUTHOR } = require('../data/author');
const { BLOG_META, BLOG_POSTS } = require('../data/blogPosts');
const { CHANGELOG_ENTRIES } = require('../data/changelogEntries');
// Browser bundle uses the small generated SEO extract instead of the full
// ~500KB marketingPages.js content module (regenerate with
// scripts/extract-marketing-lite.js). It contains every field this file
// reads: path, title, description, keywords, changefreq, priority,
// datePublished, lastmod, faqItems. The node-side twin (routeSeoData.js,
// used by the sitemap generator) keeps importing the full data module
// directly.
const { MARKETING_PAGE_SEO: MARKETING_PAGE_DEFS } = require('../data/marketingPageSeo');
const { FAQ_ITEMS } = require('./faqItems');

const SITE_NAME = 'Luckynote';
const SITE_URL = 'https://luckynote.io';
const DEFAULT_SOCIAL_IMAGE = 'https://luckynote.s3.eu-central-1.amazonaws.com/static/og-images/luckynote-og-2026-09.png';
const DEFAULT_SOCIAL_IMAGE_ALT = 'Luckynote — Everything you save, working for you.';
const FOUNDER_IMAGE_URL = `${SITE_URL}/founder-uros.jpg`;
const TWITTER_HANDLE = '@luckynotehq';
const SITE_KEYWORDS = 'message yourself,whatsapp yourself,telegram yourself,signal yourself,viber yourself,todo,to do,productivity,luckynote,lucky note,remote working,remote work tools,startup tools,diary,journal,bullet journal,bujo,gtd,getting things done,school tools,research tools,movies,series,productivity hacking,life hacks,project management,chrome bookmark extension,web bookmark extension,book tracker,inbox,luckynotes,lucky notes';
const LANDING_TITLE = 'Everything you save, working for you.';
const LANDING_DESCRIPTION = 'Send Luckynote any reel, video, or article. It finds what matters and brings it back when you need it.';

function buildFaqSchema(items) {
    if (!items?.length) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
            }
        }))
    };
}

function buildOrganizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo192.png`,
            width: 192,
            height: 192
        },
        sameAs: [
            'https://x.com/luckynotehq',
            'https://apps.apple.com/us/app/luckynote-message-yourself/id1566320984',
            'https://play.google.com/store/apps/details?id=com.luckynote',
            'https://chromewebstore.google.com/detail/luckynote/dgnamkhpffokcdhjnlebokmcgkkecncl'
        ]
    };
}

function buildWebSiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: {
            '@id': `${SITE_URL}/#organization`
        }
    };
}

function buildSoftwareApplicationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}/#softwareapplication`,
        name: SITE_NAME,
        applicationCategory: 'ProductivityApplication',
        operatingSystem: 'iOS, Android, Web',
        description: 'Chat-style note-taking app for capturing notes, links, tasks, and files in one inbox, then searching and organizing them across all devices.',
        url: SITE_URL,
        downloadUrl: 'https://apps.apple.com/us/app/luckynote-message-yourself/id1566320984',
        offers: [
            {
                '@type': 'Offer',
                url: SITE_URL,
                price: '0',
                priceCurrency: 'USD',
                name: 'Free'
            },
            {
                '@type': 'Offer',
                url: SITE_URL,
                price: '3.99',
                priceCurrency: 'USD',
                name: 'Pro monthly',
                priceSpecification: {
                    '@type': 'UnitPriceSpecification',
                    price: '3.99',
                    priceCurrency: 'USD',
                    billingIncrement: 1,
                    unitCode: 'MON'
                }
            },
            {
                '@type': 'Offer',
                url: SITE_URL,
                price: '40',
                priceCurrency: 'USD',
                name: 'Pro yearly'
            }
        ],
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.6',
            ratingCount: '6000',
            bestRating: '5',
            worstRating: '1'
        },
        author: {
            '@id': `${SITE_URL}/#organization`
        }
    };
}

function buildFounderSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        '@id': `${SITE_URL}/#founder`,
        name: AUTHOR.name,
        jobTitle: AUTHOR.jobTitle,
        url: AUTHOR.url,
        description: AUTHOR.bio,
        image: FOUNDER_IMAGE_URL,
        sameAs: AUTHOR.sameAs,
        worksFor: {
            '@id': `${SITE_URL}/#organization`
        }
    };
}

function buildGraphSchema(items) {
    const graph = (items || []).filter(Boolean).map((item) => {
        const schema = Object.assign({}, item);
        delete schema['@context'];
        return schema;
    });

    return {
        '@context': 'https://schema.org',
        '@graph': graph
    };
}

function buildWebPageSchema({ canonical, title, description, author, datePublished, dateModified, publisher }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
        inLanguage: 'en',
        primaryImageOfPage: {
            '@type': 'ImageObject',
            url: DEFAULT_SOCIAL_IMAGE
        },
        isPartOf: {
            '@id': `${SITE_URL}/#website`
        },
        about: {
            '@id': `${SITE_URL}/#softwareapplication`
        },
        ...(author ? { author } : {}),
        ...(datePublished ? { datePublished } : {}),
        ...(dateModified ? { dateModified } : {}),
        ...(publisher ? { publisher } : {})
    };
}

function buildBlogIndexSchema({ canonical, title, description }) {
    const posts = Object.entries(BLOG_POSTS).map(([slug, post], index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/blog/${slug}`,
        name: post.title
    }));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'Blog',
            '@id': `${canonical}#blog`,
            url: canonical,
            name: BLOG_META.siteName,
            description,
            inLanguage: 'en',
            publisher: {
                '@id': `${SITE_URL}/#organization`
            },
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            }
        },
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: posts
            }
        }
    ]);
}

function buildCompareIndexSchema({ canonical, title, description }) {
    const comparisons = MARKETING_PAGE_DEFS.filter((page) => page.path.startsWith('/compare/'));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: comparisons.map((page, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}${page.path}`,
                    name: page.title
                }))
            }
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Compare',
                    item: canonical
                }
            ]
        }
    ]);
}


function buildForIndexSchema({ canonical, title, description }) {
    const personas = MARKETING_PAGE_DEFS.filter((page) => page.path.startsWith('/for/'));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: personas.map((page, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}${page.path}`,
                    name: page.title
                }))
            }
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'For',
                    item: canonical
                }
            ]
        }
    ]);
}

function buildUseCasesIndexSchema({ canonical, title, description }) {
    const useCases = MARKETING_PAGE_DEFS.filter((page) => page.path.startsWith('/use-cases/'));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: useCases.map((page, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}${page.path}`,
                    name: page.title
                }))
            }
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Use cases',
                    item: canonical
                }
            ]
        }
    ]);
}

function buildMessageYourselfIndexSchema({ canonical, title, description }) {
    const messageYourselfPages = MARKETING_PAGE_DEFS.filter((page) => page.path.startsWith('/message-yourself/'));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: messageYourselfPages.map((page, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}${page.path}`,
                    name: page.title
                }))
            }
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Message yourself',
                    item: canonical
                }
            ]
        }
    ]);
}

function buildGuidesIndexSchema({ canonical, title, description }) {
    const guides = MARKETING_PAGE_DEFS.filter((page) => page.path.startsWith('/guides/'));

    return buildGraphSchema([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        {
            '@type': 'CollectionPage',
            '@id': `${canonical}#collectionpage`,
            url: canonical,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: {
                '@id': `${SITE_URL}/#website`
            },
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: guides.map((page, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}${page.path}`,
                    name: page.title
                }))
            }
        },
        {
            '@type': 'BreadcrumbList',
            '@id': `${canonical}#breadcrumb`,
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: `${SITE_URL}/`
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Guides',
                    item: canonical
                }
            ]
        }
    ]);
}

// Persona pages (/for/*), use-case pages (/use-cases/*), and comparison pages
// (/compare/*) stack a BreadcrumbList on top of the WebPage + FAQPage schema
// every marketing page already emits (plan: schema stacking).
function buildPersonaBreadcrumbSchema({ canonical, title }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'For',
                item: `${SITE_URL}/for`
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };
}

function buildUseCaseBreadcrumbSchema({ canonical, title }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'Use cases',
                item: `${SITE_URL}/use-cases`
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };
}

function buildMessageYourselfBreadcrumbSchema({ canonical, title }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'Message yourself',
                item: `${SITE_URL}/message-yourself`
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };
}

function buildGuidesBreadcrumbSchema({ canonical, title }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: `${SITE_URL}/guides`
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };
}

function buildCompareBreadcrumbSchema({ canonical, title }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'Compare',
                item: `${SITE_URL}/compare`
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: title,
                item: canonical
            }
        ]
    };
}

const MARKETING_INDEXABLE_ROUTES = MARKETING_PAGE_DEFS.map((page) => {
    const canonical = `${SITE_URL}${page.path}`;
    let breadcrumbSchema = null;
    if (page.path.startsWith('/for/')) {
        breadcrumbSchema = buildPersonaBreadcrumbSchema({ canonical, title: page.title });
    } else if (page.path.startsWith('/use-cases/')) {
        breadcrumbSchema = buildUseCaseBreadcrumbSchema({ canonical, title: page.title });
    } else if (page.path.startsWith('/compare/')) {
        breadcrumbSchema = buildCompareBreadcrumbSchema({ canonical, title: page.title });
    } else if (page.path.startsWith('/message-yourself/')) {
        breadcrumbSchema = buildMessageYourselfBreadcrumbSchema({ canonical, title: page.title });
    } else if (page.path.startsWith('/guides/')) {
        breadcrumbSchema = buildGuidesBreadcrumbSchema({ canonical, title: page.title });
    }

    const structuredData = [
        buildGraphSchema([
            buildOrganizationSchema(),
            buildWebSiteSchema(),
            buildFounderSchema(),
            buildWebPageSchema({
                canonical,
                title: page.title,
                description: page.description,
                author: {
                    '@id': `${SITE_URL}/#founder`
                },
                datePublished: page.datePublished,
                dateModified: page.lastmod,
                publisher: {
                    '@id': `${SITE_URL}/#organization`
                }
            }),
            buildFaqSchema(page.faqItems),
            breadcrumbSchema
        ])
    ].filter(Boolean);

    return {
        path: page.path,
        title: page.title,
        description: page.description,
        canonical,
        keywords: page.keywords,
        changefreq: page.changefreq || 'weekly',
        priority: page.priority || '0.8',
        datePublished: page.datePublished,
        lastmod: page.lastmod,
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData
    };
});

// Latest blog activity drives the /blog index lastmod automatically
const LATEST_BLOG_UPDATE = Object.values(BLOG_POSTS)
    .map((post) => post.updatedAt.split('T')[0])
    .sort()
    .pop();

function slugifyBlogCategory(category) {
    return String(category || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function getBlogCategoryDescription(category, count) {
    const normalizedCategory = String(category || 'blog').toLowerCase();
    const postLabel = count === 1 ? 'post' : 'posts';

    return `Read ${count} Luckynote ${postLabel} about ${normalizedCategory}, note-taking, saving ideas, and building a calmer personal knowledge system.`;
}

function getBlogCategoryEntries() {
    const categoryMap = new Map();

    Object.entries(BLOG_POSTS).forEach(([slug, post]) => {
        if (!post.category) return;

        const categorySlug = slugifyBlogCategory(post.category);
        const existing = categoryMap.get(categorySlug) || {
            name: post.category,
            slug: categorySlug,
            posts: []
        };

        existing.posts.push({
            ...post,
            slug
        });

        categoryMap.set(categorySlug, existing);
    });

    return Array.from(categoryMap.values()).map((category) => ({
        ...category,
        description: getBlogCategoryDescription(category.name, category.posts.length),
        posts: category.posts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    }));
}

function getBlogCategoryBySlug(categorySlug) {
    return getBlogCategoryEntries().find((category) => category.slug === categorySlug) || null;
}

const BLOG_CATEGORY_ROUTES = getBlogCategoryEntries().map((category) => ({
    path: `/blog/category/${category.slug}`,
    title: `${category.name} - ${BLOG_META.siteName}`,
    description: category.description,
    canonical: `${SITE_URL}/blog/category/${category.slug}`,
    keywords: `${category.name},productivity blog,note-taking tips,luckynote`,
    changefreq: 'weekly',
    priority: '0.7',
    lastmod: category.posts[0]?.updatedAt || category.posts[0]?.createdAt || LATEST_BLOG_UPDATE,
    socialImage: DEFAULT_SOCIAL_IMAGE,
    structuredData: [
        {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - ${BLOG_META.siteName}`,
            description: category.description,
            url: `${SITE_URL}/blog/category/${category.slug}`,
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: category.posts.map((post, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${SITE_URL}/blog/${post.slug}`,
                    name: post.title
                }))
            }
        }
    ]
}));

const INDEXABLE_STATIC_ROUTES = [
    {
        path: '/',
        title: LANDING_TITLE,
        description: LANDING_DESCRIPTION,
        canonical: `${SITE_URL}/`,
        preloadImage: '/landing/hero-bg.generated.webp',
        keywords: SITE_KEYWORDS,
        changefreq: 'daily',
        priority: '1.0',
        // Sitemap lastmod — bump when the landing page content meaningfully changes
        lastmod: '2026-09-28',
        structuredData: [
            buildGraphSchema([
                buildOrganizationSchema(),
                buildWebSiteSchema(),
                buildSoftwareApplicationSchema(),
                buildFounderSchema(),
                buildWebPageSchema({
                    canonical: `${SITE_URL}/`,
                    title: LANDING_TITLE,
                    description: LANDING_DESCRIPTION
                }),
                buildFaqSchema(FAQ_ITEMS)
            ])
        ]
    },
    {
        path: '/download/mobile',
        title: 'Download Luckynote for iPhone and Android',
        description: 'Download Luckynote for iPhone, iPad, and Android. Save notes, links, screenshots, and voice ideas, then find them on every device.',
        canonical: `${SITE_URL}/download/mobile`,
        changefreq: 'monthly',
        priority: '0.8',
        lastmod: '2026-09-24'
    },
    {
        path: '/download/cli',
        title: 'Luckynote CLI | Search and save from your terminal',
        description: 'Install the Luckynote CLI to search your notes, save links, and use your library from the terminal, scripts, and AI-agent workflows.',
        canonical: `${SITE_URL}/download/cli`,
        changefreq: 'monthly',
        priority: '0.7',
        lastmod: '2026-09-24'
    },
    {
        path: '/mobile',
        title: 'Download Luckynote - iPhone & Android Apps',
        description: 'Luckynote works wherever you are. Download Luckynote for iPhone and Android.',
        canonical: `${SITE_URL}/mobile`,
        changefreq: 'monthly',
        priority: '0.7',
        // Sitemap lastmod — bump when this page's content meaningfully changes
        lastmod: '2026-06-09'
    },
    {
        path: '/contact',
        title: 'Contact Us - Luckynote',
        description: 'You may at any point contact us by writing us the email at support@luckynote.io',
        canonical: `${SITE_URL}/contact`,
        changefreq: 'monthly',
        priority: '0.6',
        lastmod: '2026-03-28'
    },
    {
        path: '/privacy',
        title: 'Privacy Policy - Luckynote',
        description: 'Your privacy is important to us, so is being transparent about how we collect and use your information and data.',
        canonical: `${SITE_URL}/privacy`,
        changefreq: 'monthly',
        priority: '0.5',
        lastmod: '2026-03-28'
    },
    {
        path: '/terms',
        title: 'Terms and Conditions - Luckynote',
        description: 'Luckynote Terms and Conditions',
        canonical: `${SITE_URL}/terms`,
        changefreq: 'monthly',
        priority: '0.5',
        lastmod: '2026-03-28'
    },
    {
        path: AUTHOR.path,
        title: 'About Uros | Founder of Luckynote',
        description: 'About Uros, founder of Luckynote, and how he compares note-taking, save-later, and capture tools from real weekly use.',
        canonical: AUTHOR.url,
        changefreq: 'monthly',
        priority: '0.5',
        datePublished: '2026-07-29',
        lastmod: '2026-07-29',
        structuredData: [
            buildGraphSchema([
                buildOrganizationSchema(),
                buildWebSiteSchema(),
                buildFounderSchema(),
                {
                    '@type': 'ProfilePage',
                    '@id': `${AUTHOR.url}#profilepage`,
                    url: AUTHOR.url,
                    name: 'About Uros | Founder of Luckynote',
                    description: 'About Uros, founder of Luckynote, and how he compares note-taking, save-later, and capture tools from real weekly use.',
                    inLanguage: 'en',
                    author: {
                        '@id': `${SITE_URL}/#founder`
                    },
                    datePublished: '2026-07-29',
                    dateModified: '2026-07-29',
                    publisher: {
                        '@id': `${SITE_URL}/#organization`
                    },
                    isPartOf: {
                        '@id': `${SITE_URL}/#website`
                    },
                    mainEntity: {
                        '@id': `${SITE_URL}/#founder`
                    }
                }
            ])
        ]
    },
    {
        path: '/blog',
        title: `${BLOG_META.siteName} - Productivity Tips & Updates`,
        description: BLOG_META.description,
        canonical: `${SITE_URL}/blog`,
        keywords: 'productivity blog,note-taking tips,task management,luckynote updates,productivity tools',
        changefreq: 'daily',
        priority: '0.9',
        lastmod: LATEST_BLOG_UPDATE,
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildBlogIndexSchema({
                canonical: `${SITE_URL}/blog`,
                title: `${BLOG_META.siteName} - Productivity Tips & Updates`,
                description: BLOG_META.description
            })
        ]
    },
    {
        path: '/compare',
        title: 'Compare Luckynote to Other Note-Taking Apps',
        description:
            'See how Luckynote compares to Notion, Evernote, Obsidian, Apple Notes, Google Keep, Raindrop, Pocket, mymind, Fabric, Dropbox, Google Drive, Otter, VoiceNotes, and Pinterest.',
        canonical: `${SITE_URL}/compare`,
        keywords: 'luckynote alternative,luckynote vs,notion alternative,evernote alternative,obsidian alternative,pocket alternative,raindrop alternative',
        changefreq: 'weekly',
        priority: '0.8',
        lastmod: '2026-07-12',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildCompareIndexSchema({
                canonical: `${SITE_URL}/compare`,
                title: 'Compare Luckynote to Other Note-Taking Apps',
                description:
                    'See how Luckynote compares to Notion, Evernote, Obsidian, Apple Notes, Google Keep, Raindrop, Pocket, mymind, Fabric, Dropbox, Google Drive, Otter, VoiceNotes, and Pinterest.'
            })
        ]
    },
    {
        path: '/for',
        title: 'Who Luckynote Is For',
        description:
            'Luckynote is one chat where you send yourself everything, then makes it findable. See how it fits founders, solopreneurs, creators, ADHD brains, and more.',
        canonical: `${SITE_URL}/for`,
        keywords: 'notes app for founders,notes app for creators,notes app for solopreneurs,notes app for adhd,text yourself notes,message yourself app,who is luckynote for',
        changefreq: 'weekly',
        priority: '0.8',
        lastmod: '2026-09-29',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildForIndexSchema({
                canonical: `${SITE_URL}/for`,
                title: 'Who Luckynote Is For',
                description:
                    'Luckynote is one chat where you send yourself everything, then makes it findable. See how it fits founders, solopreneurs, creators, ADHD brains, and more.'
            })
        ]
    },
    {
        path: '/use-cases',
        title: 'Luckynote Use Cases',
        description:
            'See the workflows Luckynote handles best, from building a second brain to replacing read-it-later apps and making screenshots searchable with OCR.',
        canonical: `${SITE_URL}/use-cases`,
        keywords: 'second brain app,read it later app,organize screenshots,personal knowledge base,pocket replacement',
        changefreq: 'weekly',
        priority: '0.8',
        lastmod: '2026-07-12',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildUseCasesIndexSchema({
                canonical: `${SITE_URL}/use-cases`,
                title: 'Luckynote Use Cases',
                description:
                    'See the workflows Luckynote handles best, from building a second brain to replacing read-it-later apps and making screenshots searchable with OCR.'
            })
        ]
    },
    {
        path: '/message-yourself',
        title: 'Message Yourself, Then Find It Again | Luckynote',
        description:
            'You already text yourself notes, links, files and screenshots. Luckynote is the personal inbox built to make them searchable and useful later.',
        canonical: `${SITE_URL}/message-yourself`,
        keywords: 'message yourself notes,message yourself app,notes to self app,chat with yourself,personal inbox,AI notes search',
        changefreq: 'weekly',
        priority: '0.84',
        lastmod: '2026-07-13',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildMessageYourselfIndexSchema({
                canonical: `${SITE_URL}/message-yourself`,
                title: 'Message Yourself, Then Find It Again | Luckynote',
                description:
                    'You already text yourself notes, links, files and screenshots. Luckynote is the personal inbox built to make them searchable and useful later.'
            })
        ]
    },
    {
        path: '/guides',
        title: 'How to Get Anything into Luckynote | Luckynote',
        description:
            'Step-by-step guides for saving posts, full X threads, links, screenshots, PDFs, and files into Luckynote.',
        canonical: `${SITE_URL}/guides`,
        keywords: 'save instagram posts,save tiktok videos,save tweets,save whatsapp messages,save web pages,save screenshots,save pdf to notes app',
        changefreq: 'weekly',
        priority: '0.82',
        lastmod: '2026-09-21',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildGuidesIndexSchema({
                canonical: `${SITE_URL}/guides`,
                title: 'How to Get Anything into Luckynote | Luckynote',
                description:
                    'Step-by-step guides for saving posts, full X threads, links, screenshots, PDFs, and files into Luckynote.'
            })
        ]
    },
    {
        path: '/changelog',
        title: 'Luckynote Changelog - Product Updates and Improvements',
        description:
            'See the latest Luckynote features, improvements, and fixes.',
        canonical: `${SITE_URL}/changelog`,
        keywords: 'luckynote changelog,luckynote updates,product updates,new features',
        changefreq: 'weekly',
        priority: '0.7',
        lastmod: CHANGELOG_ENTRIES[0]?.date,
        structuredData: [
            buildWebPageSchema({
                canonical: `${SITE_URL}/changelog`,
                title: 'Luckynote Changelog - Product Updates and Improvements',
                description: 'See the latest Luckynote features, improvements, and fixes.'
            })
        ]
    },
    {
        path: '/claude',
        title: 'Connect Luckynote to Claude',
        description: 'Connect Luckynote to Claude with MCP to search, explore, and reference your notes directly in Claude.',
        canonical: `${SITE_URL}/claude`,
        keywords: 'luckynote claude,mcp notes,claude connector,model context protocol',
        changefreq: 'monthly',
        priority: '0.7',
        lastmod: '2026-09-19',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [buildWebPageSchema({ canonical: `${SITE_URL}/claude`, title: 'Connect Luckynote to Claude', description: 'Connect Luckynote to Claude with MCP to search, explore, and reference your notes directly in Claude.' })]
    },
    {
        path: '/chatgpt',
        title: 'Connect Luckynote to ChatGPT',
        description: 'Connect Luckynote to ChatGPT with MCP to search, explore, and reference your notes directly in ChatGPT.',
        canonical: `${SITE_URL}/chatgpt`,
        keywords: 'luckynote chatgpt,mcp notes,chatgpt plugins,chatgpt connector,chatgpt developer mode,model context protocol',
        changefreq: 'monthly',
        priority: '0.7',
        lastmod: '2026-09-19',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [buildWebPageSchema({ canonical: `${SITE_URL}/chatgpt`, title: 'Connect Luckynote to ChatGPT', description: 'Connect Luckynote to ChatGPT with MCP to search, explore, and reference your notes directly in ChatGPT.' })]
    },
    {
        path: '/launch',
        title: 'Support Luckynote on TinyLaunch',
        description:
            'Luckynote is launching on TinyLaunch on August 24, 2026. Join the launch reminder list and support Luckynote when it goes live.',
        canonical: `${SITE_URL}/launch`,
        keywords: 'luckynote launch,tinylaunch,luckynote tinylaunch,productivity app launch',
        changefreq: 'daily',
        priority: '0.75',
        lastmod: '2026-08-18',
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            buildWebPageSchema({
                canonical: `${SITE_URL}/launch`,
                title: 'Support Luckynote on TinyLaunch',
                description:
                    'Luckynote is launching on TinyLaunch on August 24, 2026. Join the launch reminder list and support Luckynote when it goes live.'
            })
        ]
    }
].concat(BLOG_CATEGORY_ROUTES, MARKETING_INDEXABLE_ROUTES);

const NOINDEX_ROUTE_CONFIGS = [
    {
        path: '/raycast-auth',
        title: 'Connect Raycast - Luckynote',
        robots: 'noindex,nofollow'
    },
    {
        path: '/login',
        title: 'Log In - Luckynote',
        description: 'Log in to your Luckynote account.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/register',
        title: 'Create Account - Luckynote',
        description: 'Create your Luckynote account.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/verified',
        title: 'Account Verified - Luckynote',
        description: 'Your account has been verified.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/check-account',
        title: 'Verify Account - Luckynote',
        description: 'Request a new account verification email.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/forgot-password',
        title: 'Forgot Password - Luckynote',
        description: 'Reset your Luckynote password.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/reset-password/:token',
        title: 'Reset Password - Luckynote',
        description: 'Choose a new password for your Luckynote account.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/checkout',
        title: 'Checkout - Luckynote',
        description: 'Complete your Luckynote purchase.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/purchases',
        title: 'Purchases - Luckynote',
        description: 'View your Luckynote purchase history.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/trial-feedback',
        title: 'Trial Feedback - Luckynote',
        description: 'Thank you for sharing feedback about your Luckynote trial.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/payment-success',
        title: 'Payment Success - Luckynote',
        description: 'Your Luckynote purchase was successful.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/payment-cancelled',
        title: 'Payment Cancelled - Luckynote',
        description: 'Your Luckynote purchase was cancelled.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/error',
        title: 'Service Status - Luckynote',
        description: 'Luckynote is currently unavailable.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/x-auth-error',
        title: 'X Connection Failed - Luckynote',
        description: 'There was a problem connecting your X account.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/tools/media-manager',
        title: 'Media Manager - Luckynote',
        description: 'Manage media from a saved Luckynote message.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/tools/integrations',
        title: 'Integrations - Luckynote',
        description: 'Manage your Luckynote integrations.',
        robots: 'noindex,nofollow'
    },
    {
        path: '/solucky',
        title: 'Admin - Luckynote',
        description: 'Luckynote admin.',
        robots: 'noindex,nofollow'
    }
];

function normalizeAbsoluteUrl(url) {
    if (!url) return DEFAULT_SOCIAL_IMAGE;
    if (url.startsWith('http')) return encodeURI(url);
    return encodeURI(`${SITE_URL}${url}`);
}

function getBlogPostSocialImage(post) {
    return normalizeAbsoluteUrl(post?.socialImage || post?.featuredImage || DEFAULT_SOCIAL_IMAGE);
}

function getBlogPostSeo(slug) {
    const post = BLOG_POSTS[slug];

    if (!post) {
        return null;
    }

    const canonical = `${SITE_URL}/blog/${slug}`;
    const description = post.metaDescription || post.excerpt;
    const socialImage = getBlogPostSocialImage(post);

    return {
        title: post.title,
        description,
        canonical,
        keywords: `${post.category},productivity,note-taking,luckynote,${(post.tags || []).join(',')}`,
        socialImage,
        ogType: 'article',
        extraMeta: [
            { property: 'article:author', content: post.author.name },
            { property: 'article:published_time', content: post.createdAt },
            { property: 'article:modified_time', content: post.updatedAt },
            { property: 'article:section', content: post.category }
        ].concat((post.tags || []).map((tag) => ({ property: 'article:tag', content: tag })), [
            { name: 'twitter:creator', content: TWITTER_HANDLE }
        ]),
        structuredData: [
            {
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': canonical
                },
                headline: post.title,
                description,
                image: socialImage,
                author: {
                    '@type': 'Organization',
                    name: BLOG_META.organization.name
                },
                publisher: {
                    '@type': 'Organization',
                    name: BLOG_META.organization.name,
                    logo: {
                        '@type': 'ImageObject',
                        url: BLOG_META.organization.logo
                    }
                },
                datePublished: post.createdAt,
                dateModified: post.updatedAt
            }
        ]
    };
}

function getBlogCategorySeo(categorySlug) {
    const category = getBlogCategoryBySlug(categorySlug);

    if (!category) {
        return null;
    }

    const canonical = `${SITE_URL}/blog/category/${category.slug}`;
    const title = `${category.name} - ${BLOG_META.siteName}`;

    return {
        title,
        description: category.description,
        canonical,
        keywords: `${category.name},productivity blog,note-taking tips,luckynote,${category.posts.map((post) => post.title).join(',')}`,
        changefreq: 'weekly',
        priority: '0.7',
        lastmod: category.posts[0]?.updatedAt || category.posts[0]?.createdAt || LATEST_BLOG_UPDATE,
        socialImage: DEFAULT_SOCIAL_IMAGE,
        structuredData: [
            {
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: title,
                description: category.description,
                url: canonical,
                mainEntity: {
                    '@type': 'ItemList',
                    itemListElement: category.posts.map((post, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        url: `${SITE_URL}/blog/${post.slug}`,
                        name: post.title
                    }))
                }
            }
        ]
    };
}

export {
    SITE_NAME,
    SITE_URL,
    DEFAULT_SOCIAL_IMAGE,
    DEFAULT_SOCIAL_IMAGE_ALT,
    TWITTER_HANDLE,
    SITE_KEYWORDS,
    LANDING_TITLE,
    LANDING_DESCRIPTION,
    FAQ_ITEMS,
    INDEXABLE_STATIC_ROUTES,
    NOINDEX_ROUTE_CONFIGS,
    getBlogCategorySeo,
    getBlogPostSocialImage,
    getBlogPostSeo
};
